import { actionable } from '@github/catalyst/lib/actionable'
import { targets, targetable } from '@github/catalyst/lib/targetable'

export default actionable(targetable(class extends HTMLElement {
  static [targets.static] = ['items']

  connectedCallback() {
    // Get the template content
    this.template = this.querySelector('template')
    this.container = this.querySelector('[data-dynamic-list-container]')
    
    // Get max quota from attribute, default to 10 if not specified
    this.maxQuota = parseInt(this.getAttribute('max-quota') || '10', 10)
    
    // If no container exists, create one
    if (!this.container) {
      this.container = document.createElement('div')
      this.container.className = 'space-y-4'
      this.container.setAttribute('data-dynamic-list-container', '')
      if (this.template) {
        this.template.after(this.container)
      } else {
        this.prepend(this.container)
      }
    }
    
    // Initialize recipient counter based on existing items
    this.recipientCounter = this.items.length
    
    // If we have any pre-existing items, ensure they're numbered correctly
    if (this.recipientCounter > 0) {
      this.renumberItems()
    }
    
    // Check if we need to disable the add button based on initial count
    this.updateAddButtonState()
    
    // Initialize drag and drop for the container
    this.initDragAndDrop()
  }
  
  // Initialize drag and drop event listeners on the container
  initDragAndDrop() {
    if (!this.container) return
    
    this.container.addEventListener('dragover', (e) => this.handleDragOver(e))
    this.container.addEventListener('drop', (e) => this.handleDrop(e))
  }
  
  // Called when drag starts on an item
  handleDragStart(e) {
    const item = e.target.closest('[data-targets="dynamic-list-v2.items"]')
    if (!item) return
    
    this.draggedItem = item
    item.classList.add('opacity-50', 'border-dashed', 'border-2', 'border-cs-main-500')
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', '')
  }
  
  // Called when drag ends
  handleDragEnd(e) {
    const item = e.target.closest('[data-targets="dynamic-list-v2.items"]')
    if (item) {
      item.classList.remove('opacity-50', 'border-dashed', 'border-2', 'border-cs-main-500')
    }
    
    // Remove drag-over styling from all items
    this.items.forEach(i => {
      i.classList.remove('border-t-4', 'border-b-4', 'border-cs-main-500')
    })
    
    this.draggedItem = null
    
    // Renumber items after reordering
    this.renumberItems()
  }
  
  // Handle dragover to show drop position indicator
  handleDragOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    
    const targetItem = e.target.closest('[data-targets="dynamic-list-v2.items"]')
    if (!targetItem || targetItem === this.draggedItem) return
    
    // Remove previous indicators
    this.items.forEach(i => {
      i.classList.remove('border-t-4', 'border-b-4', 'border-cs-main-500')
    })
    
    // Determine if we should show indicator above or below
    const rect = targetItem.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    
    if (e.clientY < midY) {
      targetItem.classList.add('border-t-4', 'border-cs-main-500')
    } else {
      targetItem.classList.add('border-b-4', 'border-cs-main-500')
    }
  }
  
  // Handle drop to reorder items
  handleDrop(e) {
    e.preventDefault()
    
    const targetItem = e.target.closest('[data-targets="dynamic-list-v2.items"]')
    if (!targetItem || !this.draggedItem || targetItem === this.draggedItem) return
    
    // Determine drop position
    const rect = targetItem.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    
    if (e.clientY < midY) {
      // Insert before target
      targetItem.parentNode.insertBefore(this.draggedItem, targetItem)
    } else {
      // Insert after target
      targetItem.parentNode.insertBefore(this.draggedItem, targetItem.nextSibling)
    }
    
    // Remove indicators
    this.items.forEach(i => {
      i.classList.remove('border-t-4', 'border-b-4', 'border-cs-main-500')
    })
  }

  addItem(e) {
    e.preventDefault()
    
    // Check if we've reached the maximum quota
    if (this.items.length >= this.maxQuota) {
      console.warn(`Cannot add more items: maximum quota of ${this.maxQuota} reached`)
      return
    }
    
    if (!this.template) {
      console.error('No template found for dynamic-list-v2')
      return
    }
    
    // Increment recipient counter
    this.recipientCounter++
    
    // Create new item from template
    const newItem = this.template.content.firstElementChild.cloneNode(true)
    const uniqueId = Math.floor(Math.random() * 10 ** 16)
    
    // Update input names with unique ID
    newItem.querySelectorAll('select, textarea, input').forEach((input) => {
      input.name = input.name.replace('[1]', `[${uniqueId}]`)
    })
    
    // Update recipient title with sequential number
    const autocompleteElem = newItem.querySelector('new-submitter-autocomplete') || 
                             newItem.querySelector('new-cc-submitter-autocomplete-v2') ||
                             newItem.querySelector('new-bcc-submitter-autocomplete-v2')
    if (autocompleteElem) {
      const baseTitle = autocompleteElem.getAttribute('title') || 'Recipient'
      // If the title already contains a number, replace it; otherwise append the number
      const newTitle = baseTitle.replace(/\s*\d+$/, '') + ' ' + this.recipientCounter
      autocompleteElem.setAttribute('title', newTitle)
    }
    
    // Make the new item draggable
    newItem.setAttribute('draggable', 'true')
    newItem.addEventListener('dragstart', (e) => this.handleDragStart(e))
    newItem.addEventListener('dragend', (e) => this.handleDragEnd(e))
    
    // Append to container
    this.container.appendChild(newItem)
    
    // Update add button state after adding an item
    this.updateAddButtonState()
  }

  removeItem(e) {
    e.preventDefault()
    
    const itemToRemove = this.items.find((item) => item.contains(e.target))
    if (itemToRemove) {
      // First, get the index of the item we're removing to optimize the renumbering
      const index = Array.from(this.items).indexOf(itemToRemove)
      
      // Remove the item from the DOM
      itemToRemove.remove()
      
      // Immediately renumber the remaining items
      this.renumberItemsImmediate(index)
      
      // Update add button state after removing an item
      this.updateAddButtonState()
    }
  }
  
  // Update the add button state (enable/disable) based on current item count
  updateAddButtonState() {
    const addButton = this.querySelector('[data-action="click:dynamic-list-v2#addItem"]')
    if (!addButton) return
    
    const reachedQuota = this.items.length >= this.maxQuota
    
    if (reachedQuota) {
      addButton.classList.add('btn-disabled')
      addButton.setAttribute('disabled', '')
      addButton.setAttribute('title', `Maximum of ${this.maxQuota} recipients allowed`)
    } else {
      addButton.classList.remove('btn-disabled')
      addButton.removeAttribute('disabled')
      addButton.setAttribute('title', 'Add another recipient')
    }
  }
  
  // Renumber all recipient titles sequentially
  renumberItems() {
    const items = Array.from(this.items)
    this.recipientCounter = items.length
    
    items.forEach((item, index) => {
      const autocompleteElem = item.querySelector('new-submitter-autocomplete') ||
                               item.querySelector('new-cc-submitter-autocomplete-v2') ||
                               item.querySelector('new-bcc-submitter-autocomplete-v2')
      if (autocompleteElem) {
        const baseTitle = (autocompleteElem.getAttribute('title') || 'Recipient').replace(/\s*\d+$/, '')
        autocompleteElem.setAttribute('title', baseTitle + ' ' + (index + 1))
      }
      
      // Ensure drag listeners are attached
      if (!item.hasAttribute('draggable')) {
        item.setAttribute('draggable', 'true')
        item.addEventListener('dragstart', (e) => this.handleDragStart(e))
        item.addEventListener('dragend', (e) => this.handleDragEnd(e))
      }
    })
  }
  
  // More immediate approach to renumbering - rebuild components after removal
  renumberItemsImmediate(startIndex = 0) {
    const items = Array.from(this.items)
    this.recipientCounter = items.length
    
    // Only update from the affected index onwards for efficiency
    for (let i = startIndex; i < items.length; i++) {
      const item = items[i]
      const autocompleteElem = item.querySelector('new-submitter-autocomplete') ||
                               item.querySelector('new-cc-submitter-autocomplete-v2') ||
                               item.querySelector('new-bcc-submitter-autocomplete-v2')
      
      if (autocompleteElem) {
        // Get current base name without number
        const baseTitle = (autocompleteElem.getAttribute('title') || 'Recipient').replace(/\s*\d+$/, '')
        const newTitle = baseTitle + ' ' + (i + 1)
        
        // Store current input values before replacing
        // For v2 components, also check for hidden inputs
        const nameInput = autocompleteElem.querySelector('input[name*="[name]"]') || 
                          autocompleteElem.querySelector('input[type="hidden"][name*="name"]')
        const emailInput = autocompleteElem.querySelector('input[name*="[email]"]') ||
                           autocompleteElem.querySelector('input[type="hidden"][name*="email"]')
        const nameValue = nameInput ? nameInput.value : ''
        const emailValue = emailInput ? emailInput.value : ''
        
        // Get the element's attributes
        const inputNameName = autocompleteElem.getAttribute('input-name-name')
        const inputNameEmail = autocompleteElem.getAttribute('input-name-email')
        
        // Determine element tag name
        const tagName = autocompleteElem.tagName.toLowerCase()
        
        // Create replacement element
        const newElem = document.createElement(tagName)
        newElem.setAttribute('title', newTitle)
        newElem.setAttribute('input-name-name', inputNameName)
        newElem.setAttribute('input-name-email', inputNameEmail)
        
        // Replace old element with new one
        const parentItem = autocompleteElem.parentNode
        parentItem.replaceChild(newElem, autocompleteElem)
        
        // Longer delay to allow for Vue component initialization (v2 components need more time)
        setTimeout(() => {
          // Restore values after Vue has rendered
          // For v2 components, also check for hidden inputs
          const newNameInput = newElem.querySelector('input[name*="[name]"]') ||
                               newElem.querySelector('input[type="hidden"][name*="name"]')
          const newEmailInput = newElem.querySelector('input[name*="[email]"]') ||
                                newElem.querySelector('input[type="hidden"][name*="email"]')
          if (newNameInput && nameValue) newNameInput.value = nameValue
          if (newEmailInput && emailValue) newEmailInput.value = emailValue
          
          // For v2 components, also dispatch input events to update Vue's reactive state
          if (newNameInput && nameValue) {
            newNameInput.dispatchEvent(new Event('input', { bubbles: true }))
          }
          if (newEmailInput && emailValue) {
            newEmailInput.dispatchEvent(new Event('input', { bubbles: true }))
          }
        }, 100)
      }
    }
  }
}))

